import { lazy, Suspense, useCallback, useState } from 'react';
import { Modal } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
    LayoutContextProvider,
    RoomAudioRenderer,
    RoomContext,
    useChat,
    useCreateLayoutContext,
} from '@livekit/components-react';
import type { LogLevel, Room } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { CallStage } from './call/call-stage';
import { ChatPanel } from './call/chat-panel';
import { ConnectionStatus } from './call/connection-status';
import { ControlBar } from './call/control-bar';
import { ParticipantsPanel } from './call/participants-panel';
import { ResizableSplit } from './call/resizable-split';
import { ShortcutsDialog } from './call/shortcuts-dialog';
import { type CallPanel, TopBar } from './call/top-bar';
import { CloudRecordingProvider } from './cloud-recording';
import classes from './conference-view.module.css';
import { DebugMode } from './debug';
import { KeyboardShortcuts } from './keyboard-shortcuts';
import { LobbyProvider } from './lobby/lobby';
import { LobbyNotifications } from './lobby/lobby-notifications';
import { NoiseSuppression } from './noise-suppression/noise-suppression';
import { LowerHandOnSpeech, ReactionsOverlay } from './reactions';
import { SettingsMenu } from './settings-menu';

// vite-envs переписывает import.meta.env.DEV в глобал времени выполнения, и сборка не выкидывает панель; import.meta.hot Vite заменяет на undefined при сборке.
const DevPanel = import.meta.hot ? lazy(() => import('@/dev/dev-panel')) : undefined;

export function ConferenceView({ room, logLevel }: Readonly<{ room: Room; logLevel?: LogLevel }>) {
    return (
        <RoomContext value={room}>
            <LobbyProvider>
                <CloudRecordingProvider>
                    <CallScreen />
                </CloudRecordingProvider>
            </LobbyProvider>
            <DebugMode logLevel={logLevel} />
            {DevPanel && (
                <Suspense fallback={null}>
                    <DevPanel />
                </Suspense>
            )}
        </RoomContext>
    );
}

function CallScreen() {
    const { t } = useTranslation('conference');
    const layoutContext = useCreateLayoutContext();
    const { chatMessages, send, isSending } = useChat();
    const [openPanel, setOpenPanel] = useState<CallPanel | null>(null);
    const [settingsOpened, setSettingsOpened] = useState(false);
    const [shortcutsOpened, setShortcutsOpened] = useState(false);
    const [readRemoteMessages, setReadRemoteMessages] = useState(0);
    const settingsEnabled = import.meta.env.SETTINGS_MENU_ENABLED === 'true';

    const compactViewport = useMediaQuery('(max-width: 48em)', false, { getInitialValueInEffect: false });
    const remoteMessages = chatMessages.filter((message) => !message.from?.isLocal).length;
    const chatOpen = openPanel === 'chat';
    if (chatOpen && readRemoteMessages !== remoteMessages) {
        setReadRemoteMessages(remoteMessages);
    }

    const closePanel = () => {
        setOpenPanel(null);
    };
    const togglePanel = (panel: CallPanel) => {
        setOpenPanel((current) => (current === panel ? null : panel));
    };
    const closeSettings = () => {
        setSettingsOpened(false);
    };
    const openShortcuts = () => {
        setShortcutsOpened(true);
    };
    const showLobbyQueue = useCallback(() => {
        setOpenPanel('participants');
    }, []);

    const sidePanel =
        (chatOpen && <ChatPanel isSending={isSending} messages={chatMessages} send={send} onClose={closePanel} />) ||
        (openPanel === 'participants' && <ParticipantsPanel onClose={closePanel} />);

    return (
        <div className={classes.root}>
            <LayoutContextProvider value={layoutContext}>
                <ResizableSplit
                    className={classes.split}
                    mainClassName={classes.main}
                    mainMin="50%"
                    side={!compactViewport && sidePanel}
                    sideDefault="--spacing-80"
                    sideMin="--spacing-64"
                    storageKey="meet.sidePanelSize"
                    main={
                        <>
                            <TopBar
                                openPanel={openPanel}
                                unreadMessages={chatOpen ? 0 : remoteMessages - readRemoteMessages}
                                onTogglePanel={togglePanel}
                            />
                            <CallStage />
                            <ControlBar
                                onOpenShortcuts={openShortcuts}
                                onOpenSettings={
                                    settingsEnabled
                                        ? () => {
                                              setSettingsOpened(true);
                                          }
                                        : undefined
                                }
                            />
                            <ReactionsOverlay />
                            <ConnectionStatus />
                        </>
                    }
                />
                {compactViewport && sidePanel}
                <KeyboardShortcuts onOpenShortcuts={openShortcuts} onTogglePanel={togglePanel} />
            </LayoutContextProvider>
            {settingsEnabled && (
                <Modal opened={settingsOpened} size="lg" title={t('settings.title')} onClose={closeSettings}>
                    <SettingsMenu onClose={closeSettings} />
                </Modal>
            )}
            <ShortcutsDialog
                opened={shortcutsOpened}
                onClose={() => {
                    setShortcutsOpened(false);
                }}
            />
            <LobbyNotifications onShowQueue={showLobbyQueue} />
            <LowerHandOnSpeech />
            <RoomAudioRenderer />
            <NoiseSuppression />
        </div>
    );
}
